import React from 'react';
import { ReactComponent as ExportIcon } from '@ubeya/shared-web/assets/file-excel.svg';
import { CircleIcon, ContextMenu, FlexSpace } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import { XERO_FORMAT_ACCOUNT_IDS, LABOR_DESK_ACCOUNT_ID } from '@ubeya/shared/utils/accounts';
import { HARD_CODED_ISRAEL_VAT, HARD_CODED_LABOR_DESK_VAT } from 'payroll/constants';

const SectionContainer = styled(FlexSpace)`
  flex: 1;
  font-size: 13px;
  letter-spacing: 0;
  font-weight: 500;
`;

const TimeRangeContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const Seperator = styled.div`
  margin: 0 5px;
  width: 1px;
  height: 30px;
  background-color: ${({ theme }) => theme.colors.black};
  margin: 0 10px;
`;

const DataContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PayrollTotal = styled.div`
  font-size: 14px;
`;

const Dates = styled.div`
  font-size: 14px;
`;

const ReportButtons = styled.div`
  display: flex;
  button:first-child {
    margin-right: 10px;
  }
`;

const Section = ({
  total,
  formattingMap,
  timeRange,
  clientId,
  t,
  invoicesModalParams: baseInvoicesModalParams,
  handleExportSummarizedReport,
  countryCode,
  handleExportXEROReport,
  InvoicesModalComponent
}) => {
  const [invoicesModelOpen, setInvoicesModelOpen] = useState(false);
  const { dateFormat } = useContext(ConfigContext);
  const { accountId } = useAccount();

  const handleViewDetailedReport = useCallback((e) => {
    e.stopPropagation();
    setInvoicesModelOpen(true);
  }, []);

  const invoicesModalParams = {
    ...baseInvoicesModalParams,
    onClose: () => setInvoicesModelOpen(false),
    timeRange,
    countryCode
  };

  const startDate = timeRange.start.format(dateFormat);
  const endDate = timeRange.end.format(dateFormat);

  const exportOptions = useMemo(
    () => [
      {
        label: t('exportSummarizedReport'),
        onClick: handleExportSummarizedReport
      },

      {
        label: t('exportFullReport'),
        onClick: handleViewDetailedReport
      },
      {
        label: t('exportToXeroFormat'),
        onClick: handleExportXEROReport,
        visible: !!clientId && XERO_FORMAT_ACCOUNT_IDS.includes(accountId)
      }
    ],
    [accountId, clientId, handleExportSummarizedReport, handleExportXEROReport, handleViewDetailedReport, t]
  );

  return (
    <SectionContainer>
      <DataContainer>
        <TimeRangeContainer>
          <Dates>{`${startDate} - ${endDate}`}</Dates>
        </TimeRangeContainer>
        <Seperator />

        <PayrollTotal>
          {formattingMap.currency(total, { withSpace: false, includeZero: true })}
          {(countryCode?.toLowerCase() === 'il' || accountId === LABOR_DESK_ACCOUNT_ID) &&
            ` (${formattingMap.currency(
              total * (accountId === LABOR_DESK_ACCOUNT_ID ? HARD_CODED_LABOR_DESK_VAT : HARD_CODED_ISRAEL_VAT),
              { withSpace: false, includeZero: true }
            )} ${t('vat')})`}
        </PayrollTotal>
      </DataContainer>

      <ReportButtons>
        <ContextMenu options={exportOptions}>
          <CircleIcon icon={ExportIcon} />
        </ContextMenu>
      </ReportButtons>

      {invoicesModelOpen && <InvoicesModalComponent {...invoicesModalParams} />}
    </SectionContainer>
  );
};

export default Section;
